import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { applyBusinessValidation } from '@shared/validation/businessRules';
import { formatBs, syncBillDateEdit } from '@shared/date/bikramSambat';
import type { PaymentMethod } from '@shared/schema/billData';
import type { UserProfile } from '@shared/schema/userProfile';
import { useScanSession } from '@/features/scanner/ScanSessionContext';
import EditableField from '@/components/EditableField';
import { getProfile } from '@/lib/db/profileRepo';
import { createBill } from '@/lib/db/billsRepo';
import { findDuplicates, type DuplicateMatch } from '@/services/bills/duplicateDetection';
import { parseUserAmount } from '@/utils/parseAmount';

type StringFieldKey = 'billNumber' | 'sellerPan';

export default function ReviewPage() {
  const navigate = useNavigate();
  const { prepared, billData, updateBillData } = useScanSession();
  const [profile, setProfile] = useState<UserProfile | null | undefined>(undefined);
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([]);
  const [acknowledgeDuplicate, setAcknowledgeDuplicate] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!billData || !prepared) navigate('/scan', { replace: true });
  }, [billData, prepared, navigate]);

  useEffect(() => {
    void getProfile().then(setProfile);
  }, []);

  const sellerPan = billData?.sellerPan.value ?? null;
  const billNumber = billData?.billNumber.value ?? null;
  const amount = billData?.amount.value ?? null;
  const billDateAd = billData?.billDateAd.value ?? null;

  useEffect(() => {
    if (!billData) return;
    setAcknowledgeDuplicate(false);
    void findDuplicates(billData).then(setDuplicates);
    // Re-check only when the fields that matter for duplicate matching change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerPan, billNumber, amount, billDateAd]);

  const businessErrors = useMemo(() => (billData ? applyBusinessValidation(billData) : []), [billData]);
  const errorsByField = useMemo(() => Object.fromEntries(businessErrors.map((e) => [e.field, e.message])), [businessErrors]);

  if (!billData || !prepared) return null;

  function updateStringField(key: StringFieldKey, newValue: string) {
    if (!billData) return;
    const trimmed = newValue;
    updateBillData({
      ...billData,
      [key]: { ...billData[key], value: trimmed.length > 0 ? trimmed : null, status: 'user_edited' },
    });
  }

  /**
   * Keeps billDateAd and billDateBs in sync whenever either is edited — IRD's
   * form only accepts AD (BS is shown as a convenience for the user's own
   * calendar), so if only the edited field were saved the other would go
   * stale and silently fail (or wrongly pass) the fiscal-year check below,
   * which validates billDateAd. See shared/src/date/bikramSambat.ts.
   */
  function updateBillDate(edited: 'ad' | 'bs', newValue: string) {
    if (!billData) return;
    const rawKey = edited === 'bs' ? 'billDateBs' : 'billDateAd';
    const synced = syncBillDateEdit(edited, newValue);
    if (synced === null) {
      // Still mid-typing an incomplete date — reflect the keystroke without
      // touching the other field (avoids blanking it out char-by-char).
      updateBillData({
        ...billData,
        [rawKey]: { ...billData[rawKey], value: newValue.length > 0 ? newValue : null, status: 'user_edited' },
      });
      return;
    }
    updateBillData({
      ...billData,
      billDateAd: { ...billData.billDateAd, value: synced.billDateAd, status: 'user_edited' },
      billDateBs: { ...billData.billDateBs, value: synced.billDateBs, status: 'user_edited' },
    });
  }

  function updateAmount(newValue: string) {
    if (!billData) return;
    updateBillData({
      ...billData,
      amount: { ...billData.amount, value: parseUserAmount(newValue), status: 'user_edited' },
    });
  }

  function updatePaymentMethod(method: PaymentMethod) {
    if (!billData) return;
    updateBillData({
      ...billData,
      paymentMethod: { ...billData.paymentMethod, value: method, status: 'user_edited' },
    });
  }

  const hasExactDuplicate = duplicates.some((d) => d.severity === 'exact');
  const hasBusinessErrors = businessErrors.length > 0;
  const needsDuplicateAck = duplicates.length > 0 && !acknowledgeDuplicate;
  const isElectronic = billData.paymentMethod.value === 'electronic';
  const canConfirm = !hasBusinessErrors && !needsDuplicateAck && !isElectronic && !saving;

  async function handleConfirm() {
    if (!billData || !canConfirm) return;
    setSaving(true);
    try {
      const record = await createBill({
        billData,
        imageThumbnail: prepared?.thumbnail,
        imageFull: prepared?.original,
      });
      // Deliberately NOT calling reset() here: doing so while ReviewPage is
      // still mounted forces a re-render with billData=null in the same
      // batch as this navigate() call, which re-triggers ReviewPage's own
      // guard effect ("if (!billData) navigate('/scan')") and overwrites
      // this history entry, bouncing the user back to /scan instead of
      // landing on /ird/:id. The next scan's setPrepared() naturally
      // overwrites this session state anyway, so nothing leaks long-term.
      navigate(`/ird/${record.id}`, { replace: true });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 p-4 pt-6">
      <header>
        <h1 className="text-xl font-bold text-slate-900">Review Bill</h1>
        <p className="mt-1 text-sm text-slate-500">Check every value against your bill and correct anything that's wrong.</p>
      </header>

      {isElectronic && (
        <div role="alert" className="rounded-xl bg-blue-50 p-4 text-sm text-blue-900">
          <p className="font-semibold">This looks like an electronic (QR/card) payment.</p>
          <p className="mt-1">
            IRD automatically enrolls QR and card payments — there's nothing to submit here. If this was actually a
            cash payment, correct it below.
          </p>
        </div>
      )}

      <section className="flex flex-col gap-5 rounded-2xl bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Bill Information</h2>

        <EditableField
          label="Bill Number"
          value={billData.billNumber.value ?? ''}
          status={billData.billNumber.status}
          confidence={billData.billNumber.confidence}
          evidence={billData.billNumber.evidence}
          error={errorsByField.billNumber}
          onChange={(v) => updateStringField('billNumber', v)}
          hint={billData.billNumber.status === 'missing' ? "We couldn't confidently identify the bill number. Please enter it manually." : undefined}
        />

        <EditableField
          label="VAT / PAN Number"
          value={billData.sellerPan.value ?? ''}
          status={billData.sellerPan.status}
          confidence={billData.sellerPan.confidence}
          evidence={billData.sellerPan.evidence}
          error={errorsByField.sellerPan}
          inputMode="numeric"
          onChange={(v) => updateStringField('sellerPan', v)}
          hint={billData.sellerPan.status === 'missing' ? 'PAN/VAT could not be identified. Please enter it manually.' : undefined}
        />

        <EditableField
          label="Amount Paid"
          value={billData.amount.value === null ? '' : String(billData.amount.value)}
          status={billData.amount.status}
          confidence={billData.amount.confidence}
          evidence={billData.amount.evidence}
          error={errorsByField.amount}
          inputMode="decimal"
          onChange={updateAmount}
        />

        <EditableField
          label="Bill Date (Bikram Sambat)"
          value={billData.billDateBs.value ?? ''}
          status={billData.billDateBs.status}
          confidence={billData.billDateBs.confidence}
          evidence={billData.billDateBs.evidence}
          error={errorsByField.billDateAd}
          onChange={(v) => updateBillDate('bs', v)}
          hint={
            billData.billDateBs.value && !errorsByField.billDateAd
              ? formatBsDateSafely(billData.billDateBs.value)
              : billData.billDateBs.status === 'missing'
                ? "We couldn't identify a valid bill date. Please enter it manually (YYYY-MM-DD)."
                : undefined
          }
        />

        <EditableField
          label="Bill Date (AD)"
          value={billData.billDateAd.value ?? ''}
          status={billData.billDateAd.status}
          confidence={billData.billDateAd.confidence}
          evidence={billData.billDateAd.evidence}
          error={errorsByField.billDateAd}
          onChange={(v) => updateBillDate('ad', v)}
        />

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-slate-700">Payment Method</span>
          <div className="flex gap-2">
            {(['cash', 'electronic'] as const).map((method) => (
              <button
                key={method}
                type="button"
                onClick={() => updatePaymentMethod(method)}
                className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-medium capitalize ${
                  billData.paymentMethod.value === method
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-slate-300 bg-white text-slate-600'
                }`}
              >
                {method}
              </button>
            ))}
          </div>
        </div>
      </section>

      {duplicates.length > 0 && (
        <div role="alert" className="flex flex-col gap-2 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">
            {hasExactDuplicate ? 'This bill may already have been submitted.' : 'A similar bill was found in your history.'}
          </p>
          <p>Same seller PAN, and a matching bill number, amount, or date as {duplicates.length === 1 ? 'a bill' : 'bills'} already saved.</p>
          <label className="mt-1 flex items-center gap-2">
            <input type="checkbox" checked={acknowledgeDuplicate} onChange={(e) => setAcknowledgeDuplicate(e.target.checked)} />
            I've checked — this is a different bill.
          </label>
        </div>
      )}

      <section className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Your Information</h2>
        {profile === undefined ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : profile === null ? (
          <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
            <p>You haven't set up your profile yet — add your name, address, and mobile number in Settings.</p>
            <button type="button" onClick={() => navigate('/settings')} className="mt-2 font-semibold text-blue-700">
              Go to Settings →
            </button>
          </div>
        ) : (
          <dl className="grid grid-cols-1 gap-3 text-sm">
            <div>
              <dt className="text-slate-500">Name</dt>
              <dd className="font-medium text-slate-900">{profile.name}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Address</dt>
              <dd className="font-medium text-slate-900">{profile.address}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Mobile Number</dt>
              <dd className="font-medium text-slate-900">{profile.mobileNumber}</dd>
            </div>
          </dl>
        )}
      </section>

      <button
        type="button"
        onClick={() => void handleConfirm()}
        disabled={!canConfirm || !profile}
        className="rounded-2xl bg-blue-700 px-6 py-5 text-lg font-semibold text-white shadow-sm active:bg-blue-800 disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        {saving ? 'Saving…' : 'Confirm Information'}
      </button>
    </div>
  );
}

function formatBsDateSafely(iso: string): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return undefined;
  try {
    return formatBs({ year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) });
  } catch {
    return undefined;
  }
}
